/**
 * Running a *change* — the other half of `useAsync`.
 *
 * A read wants to run on its own as soon as the screen appears. A write wants to
 * run when a button is pressed, disable that button while it is in flight, and
 * leave its failure on screen next to the form rather than replacing the screen
 * with an error page. That is a different shape, so it is a different hook.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

export interface SubmitState<Args extends unknown[], T> {
  /** Runs it. Resolves with the value, or `undefined` if it failed. */
  submit: (...args: Args) => Promise<T | undefined>;
  /** True while in flight. Disable the button with this. */
  pending: boolean;
  /** The last failure, or `undefined`. Cleared when the next attempt starts. */
  error: Error | undefined;
  /** Dismisses the error without retrying. */
  clearError: () => void;
}

/**
 * Wraps an async action for a button or a form.
 *
 * **It does not re-throw.** A rejected promise from a click handler is an
 * unhandled rejection and nothing on screen changes; returning `undefined` makes
 * the failure a value the caller must look at:
 *
 * ```tsx
 * const saved = await save(name);
 * if (saved) close();
 * ```
 */
export function useSubmit<Args extends unknown[], T>(
  operation: (...args: Args) => Promise<T>,
): SubmitState<Args, T> {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<Error | undefined>(undefined);

  const latest = useRef(operation);
  latest.current = operation;

  // A dialog is routinely closed by the very action running inside it, so by the
  // time the promise settles this hook may be gone. Writing state then is a
  // React warning and nothing else useful, so the settle path checks first.
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const submit = useCallback(async (...args: Args): Promise<T | undefined> => {
    setPending(true);
    setError(undefined);
    try {
      return await latest.current(...args);
    } catch (cause: unknown) {
      if (mounted.current) {
        setError(cause instanceof Error ? cause : new Error(String(cause)));
      }
      return undefined;
    } finally {
      if (mounted.current) setPending(false);
    }
  }, []);

  const clearError = useCallback(() => setError(undefined), []);

  return { submit, pending, error, clearError };
}
